'use client';

/**
 * ProductBuyButton — client-side wrapper for the "Comprar" button on the
 * product detail page.
 *
 * Spec ref: SPEC-081 Fase 4 §6.6 (Enable "Comprar" button on product
 *           detail), §6.1 (checkout modal — opens on click);
 *           SPEC-081 Fase 5 §6 Task C (3 checkout variants — physical
 *           3-step, service 3-step with date picker + guestCount, digital
 *           1-step with summary + 2FA); SPEC-NEGOCIO §10.6 (Flujo de
 *           Compra Producto Físico), §10.7 (Servicios — INV-MKT-14/15),
 *           §10.8 (Digitales — INV-MKT-14).
 *           SPEC-094 §1.1 (Market Público — Buy button requires auth:
 *           if the visitor is NOT authenticated, the button becomes a
 *           "Inicia sesión para comprar" link that redirects to
 *           `/login?next=<current URL>` so the user can log in + return
 *           to this product to complete the purchase).
 *
 * Why a separate client component?
 *   The product detail page (`page.tsx`) is a Server Component (async
 *   function + `generateMetadata` + `fetch` with `revalidate: 60` for
 *   ISR/SEO). The "Comprar" button needs `useState` (to toggle the
 *   checkout modal) + `useAuth` (to detect if the visitor is
 *   authenticated) + `'use client'` directives, which a Server Component
 *   can't have. Extracting just the button + modal into a client
 *   component keeps the page SSR/ISR intact (the button renders server-
 *   side as an enabled button with the same ARIA labels; the client takes
 *   over only for the click → modal open interaction + auth detection).
 *
 * Behavior (Fase 5 — Task C1) — AUTHENTICATED visitor:
 *   - `product.type === 'physical'`: enabled "Comprar ahora" button.
 *     On click, opens the `<CheckoutModal />` with the product + seller's
 *     fulfillment flags. The modal renders the physical variant (3-step:
 *     details → summary → 2FA → create physical order).
 *   - `product.type === 'service'`: enabled "Reservar servicio" button.
 *     On click, opens the modal in service variant (3-step: reservation
 *     details with date picker + time range + guestCount → summary → 2FA
 *     → create service order with 50% deposit).
 *   - `product.type === 'digital'`: enabled "Comprar y descargar" button.
 *     On click, opens the modal in digital variant (1-step: summary + 2FA
 *     → create digital order → redirect to order detail page where the
 *     download button is shown).
 *
 * SPEC-157 — Venta única recurrente:
 *   When the product is flagged `singlePurchaseRecurring`, the backend
 *   (escrow.service.ts `assertSinglePurchaseAllowed`) blocks 2das compras
 *   del mismo usuario with 409 `SINGLE_PURCHASE_ALREADY_BOUGHT`. The 409
 *   is intercepted in the `<CheckoutModal />` create call's catch block
 *   (one per variant — physical/service/digital) — it surfaces a friendly
 *   `toast.info('Ya eres cliente de este servicio')` + closes the modal
 *   (the buyer is NOT in an error state, they are an existing customer
 *   who needs to coordinate recurring payments off-platform with the
 *   vendedor). This component does NOT call `api.market.orders.create`
 *   directly — it just renders the button + delegates the create to the
 *   `<CheckoutModal />`. The intercept lives there.
 *
 * Behavior (SPEC-094 §1.1) — UNAUTHENTICATED visitor:
 *   The button becomes a link to `/login?next=<current URL>` with the
 *   label "Inicia sesión para comprar". After login, the user is
 *   redirected back to this product page (via the `next` query param)
 *   where they can complete the purchase. The label changes per
 *   product type:
 *     - `physical`: "Inicia sesión para comprar"
 *     - `service`:  "Inicia sesión para reservar"
 *     - `digital`:   "Inicia sesión para descargar"
 *
 * §6 Anti-Invención:
 *   - NO `as any` (P-041) — `useState` + `Route` casts where applicable.
 *   - NO `console.*` (P-042).
 *   - ARIA labels (P-044) — the button has an explicit `aria-label` that
 *     includes the product title + the action verb.
 *   - NO business logic — just a thin UI shell that opens the modal.
 *   - The 3 button labels + flow dispatch are dictated by SPEC-081 Fase 5
 *     §6 Task C (UI shape) + §4.1/§4.2 (per-type flows). The button does
 *     NOT decide anything beyond "which label + which variant" — the
 *     CheckoutModal owns the actual flow (steps, validation, 2FA, create
 *     call).
 *   - SPEC-094 §1.1: the "Inicia sesión para comprar" affordance is a
 *     pure UX gate (NO business logic) — the backend independently
 *     rejects unauthenticated POST /api/market/orders with 401. The
 *     client-side gate is a UX nicety (avoids a confusing modal that
 *     fails on submit) + an attribution opportunity (the `next` param
 *     returns the user to this product after login).
 */

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { ShoppingBag, LogIn, AlertTriangle } from 'lucide-react';
import type { PublicProductResponse } from '@profileia/shared';
import { CheckoutModal } from '@/components/market/checkout-modal';
import { useAuth } from '@/lib/use-auth';

export interface ProductBuyButtonProps {
  /** The product being viewed. Drives the button label + modal variant. */
  product: PublicProductResponse;
}

/**
 * Resolve the per-type UI affordance — button label + helper subtitle.
 * Mirrors SPEC-081 Fase 5 §6 Task C verbatim (NO invented labels).
 *
 * SPEC-094 §1.1: when the visitor is unauthenticated, the labels switch
 * to "Inicia sesión para {verb}" — same per-type verb (comprar / reservar
 * / descargar) so the UX intent is preserved.
 */
function resolveCopy(product: PublicProductResponse, isAuthenticated: boolean): {
  buttonLabel: string;
  ariaLabel: string;
  subtitle: string;
} {
  // SPEC-094 §1.1 — unauthenticated variants. The verb (comprar / reservar
  // / descargar) is preserved so the user understands what action they'll
  // be able to take after logging in.
  if (!isAuthenticated) {
    switch (product.type) {
      case 'service':
        return {
          buttonLabel: 'Inicia sesión para reservar',
          ariaLabel: `Inicia sesión para reservar el servicio: ${product.title}`,
          subtitle:
            'Necesitas una cuenta para reservar este servicio · Depósito 50% al reservar',
        };
      case 'digital':
        return {
          buttonLabel: 'Inicia sesión para descargar',
          ariaLabel: `Inicia sesión para comprar y descargar: ${product.title}`,
          subtitle:
            'Necesitas una cuenta para comprar y descargar · Descarga inmediata tras el pago',
        };
      case 'physical':
      default:
        return {
          buttonLabel: 'Inicia sesión para comprar',
          ariaLabel: `Inicia sesión para comprar: ${product.title}`,
          subtitle:
            'Necesitas una cuenta para comprar este producto · Escrow protegido',
        };
    }
  }

  // Authenticated variants — unchanged from pre-SPEC-094 behavior.
  switch (product.type) {
    case 'service':
      return {
        buttonLabel: 'Reservar servicio',
        ariaLabel: `Reservar servicio: ${product.title}`,
        subtitle:
          'Depósito 50% al reservar · Balance 50% al completar · Confirmación del vendedor en 72h',
      };
    case 'digital':
      return {
        buttonLabel: 'Comprar y descargar',
        ariaLabel: `Comprar y descargar: ${product.title}`,
        subtitle:
          'Pago único con créditos · Descarga inmediata · Escrow liberado al descargar',
      };
    case 'physical':
    default:
      return {
        buttonLabel: 'Comprar ahora',
        ariaLabel: `Comprar ${product.title}`,
        subtitle:
          'Pago con créditos · Escrow protegido · Confirmación del vendedor en 72h',
      };
  }
}

export function ProductBuyButton({ product }: ProductBuyButtonProps) {
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const { user, loading } = useAuth();
  const router = useRouter();

  // SPEC-094 §1.1: during the auth-resolution window (SSR + the first
  // client render before `/api/auth/me` resolves), we don't yet know if
  // the visitor is authenticated. We render the AUTHENTICATED button
  // (label + behavior) so the SSR HTML matches the first client render
  // (no hydration mismatch). After `loading` flips to `false`, the
  // button re-renders with the correct affordance.
  //
  // This means unauthenticated visitors see a brief flash of "Comprar
  // ahora" before the button switches to "Inicia sesión para comprar".
  // That's acceptable UX (the auth resolution typically takes <500ms
  // on a warm session, and the alternative — a full-page spinner —
  // feels slower for the unauthenticated majority of /market visitors).
  const isAuthenticated = loading ? true : !!user;

  // sellerFulfillment is passed explicitly to the modal per the Task C1
  // contract — redundant with `product.seller.fulfillmentOptions` but
  // self-documents the buyer's available options at the call site. Only
  // relevant for the physical variant (service + digital ignore it).
  const sellerFulfillment = {
    ships: product.seller.fulfillmentOptions.ships,
    pickup: product.seller.fulfillmentOptions.pickup,
  };

  const copy = resolveCopy(product, isAuthenticated);

  // SPEC-094 §1.1 — unauthenticated path: the button becomes a link to
  // `/login?next=<current URL>`. After login, the user is redirected
  // back to this product page where they can complete the purchase.
  // We use `router.push` (instead of a `<Link>`) because the button
  // already has its own click handler — wrapping it in a `<Link>` would
  // create invalid HTML (`<a>` inside `<button>`). The current URL is
  // captured client-side via `window.location.pathname + search` so the
  // `next` param is the full URL the user was viewing (including any
  // `?ref=` attribution query param).
  const handleUnauthenticatedClick = () => {
    if (typeof window === 'undefined') return;
    const currentPath = window.location.pathname + window.location.search;
    router.push(`/login?next=${encodeURIComponent(currentPath)}`);
  };

  return (
    <>
      <div className="mt-4">
 {/* P-137 fix (): when the product is a legacy digital
            marked `needsReupload=true`, the buy button is HIDDEN + a
            warning banner is shown instead. The backend independently
            rejects the order with 409 PRODUCT_TEMPORARILY_UNAVAILABLE —
            this is a UX affordance so the buyer doesn't open the modal +
            pay only to see the error. The banner text is user-facing
            (anti-P-083: NO internal codenames like "SPEC", "P-137",
            "needsReupload", "S3" — just a clear "temporalmente no
            disponible" message). */}
        {product.type === 'digital' &&
        product.digitalConfig?.needsReupload === true ? (
          <div
            role="alert"
            aria-live="polite"
            className="w-full rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-900/60 dark:bg-amber-950/30 px-4 py-3 text-sm flex items-start gap-3"
          >
            <AlertTriangle
              className="size-5 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0"
              aria-hidden="true"
            />
            <div className="flex-1">
              <p className="font-semibold text-amber-800 dark:text-amber-300">
                Este producto digital está temporalmente no disponible.
              </p>
              <p className="text-amber-700 dark:text-amber-400/90 mt-0.5">
                El vendedor está actualizando el archivo.
              </p>
            </div>
          </div>
        ) : isAuthenticated ? (
          <button
            type="button"
            onClick={() => setCheckoutOpen(true)}
            className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-primary text-primary-foreground px-6 py-3 text-sm font-medium hover:bg-primary/90 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            aria-label={copy.ariaLabel}
          >
            <ShoppingBag className="size-4" aria-hidden="true" />
            {copy.buttonLabel}
          </button>
        ) : (
          <button
            type="button"
            onClick={handleUnauthenticatedClick}
            className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-primary text-primary-foreground px-6 py-3 text-sm font-medium hover:bg-primary/90 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            aria-label={copy.ariaLabel}
          >
            <LogIn className="size-4" aria-hidden="true" />
            {copy.buttonLabel}
          </button>
        )}
        {/* P-137 fix: hide the subtitle ("Pago único con créditos...")
            when the banner is shown (the banner replaces the button — no
            buy flow = no need for the subtitle). */}
        {!(product.type === 'digital' && product.digitalConfig?.needsReupload === true) && (
          <p className="mt-2 text-center text-xs text-muted-foreground">
            {copy.subtitle}
          </p>
        )}
      </div>

 {/* : the checkout modal is only mounted when the
          visitor is authenticated. When unauthenticated, mounting the
          modal would be wasted work (the user can't open it). The modal
          is also kept mounted (rather than conditionally rendered inside
          the `isAuthenticated` branch above) so the open→close animation
          works correctly when the auth state changes mid-session. */}
      {isAuthenticated && (
        <CheckoutModal
          open={checkoutOpen}
          onClose={() => setCheckoutOpen(false)}
          product={product}
          sellerFulfillment={sellerFulfillment}
        />
      )}
    </>
  );
}
